import translate from '@translate';
import React, { FC, MouseEvent, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import { styled } from '@mui/system';
import Checkbox from 'src/components/Checkbox';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import { colors, sizes } from 'src/themes/default';
import {
    formatEnabledShippersSummary,
    parseEnabledShippersCsv,
    toggleEnabledShipper,
    uniqueShipperCodes,
} from './enabledShippersPicker';

const TriggerButton = styled('button')({
    display: 'inline-flex',
    alignItems: 'center',
    height: '17px',
    maxWidth: '140px',
    padding: '0 6px',
    margin: 0,
    border: `${sizes.border} solid ${colors.text_T3}`,
    borderRadius: sizes.radius,
    backgroundColor: colors.background_W,
    color: colors.text_T1,
    fontSize: sizes.small_FS,
    lineHeight: '17px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    cursor: 'pointer',
});

const Panel = styled(Box)({
    minWidth: '220px',
    maxHeight: '280px',
    overflowY: 'auto',
    padding: '8px 12px',
});

const Row = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    minHeight: sizes.body_LH,
    cursor: 'pointer',
});

const Label = styled('span')({
    fontSize: sizes.body_FS,
    lineHeight: sizes.body_LH,
    color: colors.text_T1,
});

export interface EnabledShippersPickerPropsType {
    value: string;
    serviceCodes: ConstantsCodeType[];
    onChange: (csv: string) => void;
    disabled: boolean;
}

const EnabledShippersPicker: FC<EnabledShippersPickerPropsType> = (props) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const options = useMemo(
        () => uniqueShipperCodes(props.serviceCodes, props.value),
        [props.serviceCodes, props.value]
    );
    const selected = useMemo(() => parseEnabledShippersCsv(props.value), [props.value]);
    const selectedUpper = useMemo(() => new Set(selected.map((code) => code.toUpperCase())), [selected]);
    const summary = formatEnabledShippersSummary(selected);

    const handleOpen = (event: MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (!props.disabled) {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleToggle = (shipperCode: string) => {
        if (props.disabled) {
            return;
        }
        props.onChange(toggleEnabledShipper(props.value, shipperCode, options));
    };

    const stopTreeToggle = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
    };

    return (
        <Box onClick={stopTreeToggle} onMouseDown={stopTreeToggle}>
            <TriggerButton
                type="button"
                data-testid="system-config-enabled-shippers-trigger"
                onClick={handleOpen}
                disabled={props.disabled}
                title={selected.join(', ') || translate('systemConfigDetail.enabledShippers.none')}
            >
                {summary || translate('systemConfigDetail.enabledShippers.none')}
            </TriggerButton>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Panel data-testid="system-config-enabled-shippers-panel">
                    <Box sx={{ fontWeight: sizes.bold_W, marginBottom: '8px' }}>
                        {translate('systemConfigDetail.enabledShippers.title')}
                    </Box>
                    {options.map((shipperCode) => {
                        const checked = selectedUpper.has(shipperCode.toUpperCase());
                        return (
                            <Row
                                key={shipperCode}
                                data-testid={`system-config-enabled-shippers-checkbox-${shipperCode}`}
                                onClick={() => handleToggle(shipperCode)}
                            >
                                <Checkbox
                                    value={checked}
                                    onChange={() => handleToggle(shipperCode)}
                                    sx={{
                                        padding: 0,
                                        width: '18px',
                                        height: '18px',
                                        '& .MuiSvgIcon-root': {
                                            fontSize: '18px',
                                        },
                                    }}
                                />
                                <Label>{shipperCode}</Label>
                            </Row>
                        );
                    })}
                </Panel>
            </Popover>
        </Box>
    );
};

export default EnabledShippersPicker;
